import type { MutationResult } from '@/graphql/types';
import type { SignatoryEntry } from '@/modules/templates/lib/signatory-config';

// ---------------------------------------------------------------------------
// Sentinel values
// ---------------------------------------------------------------------------

// Used as OrganizationId on guest-owned documents. Existing org-scoped reads
// filter by `OrganizationId: orgUDSId` and naturally won't match this value.
export const GUEST_ORG_SENTINEL = '__guest__';

// ---------------------------------------------------------------------------
// Serialized JSON sub-types
// ---------------------------------------------------------------------------

/**
 * Detected location of a signatory's signature in the generated PDF (points,
 * top-left origin). Present on fixed-DOCX contracts where the template marked a
 * signature anchor; resolved from the anchor text at generation time.
 */
export interface ContractSignatureLocation {
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
}

export interface ContractSignatory {
  id: string;
  partyType: string;
  name: string;
  email: string;
  title?: string;
  phone?: string;
  company?: string;
  address?: string;
  status: string;
  updatedTime: string;
  /** Signing order (1-based) when the template defines one. */
  order?: number | null;
  /**
   * The template's person-variable name this signatory came from. Keeps a
   * stable link back to the source signature marker, so an anchor can still be
   * matched when two signatories share identical anchor text.
   */
  templateKey?: string;
  /** Resolved PDF signature-box location (fixed-DOCX flow); null when not found/placed. */
  signatureLocation?: ContractSignatureLocation | null;
  /**
   * Which coordinate space `signatureLocation` is in — the two are NOT
   * interchangeable.
   *
   * `'user'`  — the person dragged the box in the .docx review, so the value is
   *             in docx-preview's space and the review must seed from it again.
   * `'detected'` — found by `detectSignatureLocations` on the converted PDF, so
   *             the value is in LibreOffice's space. Correct for the signature
   *             service and for viewing the finished PDF; WRONG to replay into
   *             the .docx preview, which paginates differently.
   *
   * Absent on rows written before this distinction existed. Those are treated
   * as `'detected'`, so the review re-resolves them from the anchor instead of
   * trusting coordinates whose origin is unknown.
   */
  signatureLocationSource?: 'user' | 'detected';
  /** PDF date-box location placed alongside the signature (fixed-DOCX flow). */
  dateLocation?: ContractSignatureLocation | null;
  /**
   * Text-context anchor of this signatory's signature marker in the source .docx. Persisted
   * so the editable review can RE-DETECT the signature's PDF coordinates after the document
   * is edited and reflowed (the stored `signatureLocation` becomes stale after any edit).
   */
  signatureAnchor?: ContractSignatureAnchor | null;
}

/** Text-context anchor for a signature marker (mirrors DocxSourceSpan). */
export interface ContractSignatureAnchor {
  paragraphIndex: number | null;
  originalText: string;
  contextBefore: string;
  contextAfter: string;
}

// ---------------------------------------------------------------------------
// UDS Entity
// ---------------------------------------------------------------------------

export interface OrgContractDocumentItem {
  ItemId: string;
  CreatedDate?: string;
  LastUpdatedDate?: string;
  CreatedBy?: string;
  LastUpdatedBy?: string;
  IsDeleted?: boolean;
  Language?: string;
  OrganizationIds?: string[];
  Tags?: string[];

  OrganizationId: string;
  SigOrgId?: string; // SELISE Signature (ARC) OrganizationId of the owning org
  GuestSessionId?: string | null;
  Name: string;
  TemplateId?: string;
  TemplateSlug: string;
  TemplateTitle: string;
  Status: string;
  IsArchived: boolean;
  IsInTrash: boolean;
  FormData: string;
  EnrichmentContext?: string;
  DynamicFields?: string;
  CurrentStep?: number;
  ProgressPercent: number;
  Document: string;
  Signatories: string;
  SigDocumentId?: string;
  SigDocumentUrl?: string;
  CompletedAt?: string;
  HasSentForApproval?: boolean;
  IsApproved?: boolean;
  Reviewers?: string[];
}

// ---------------------------------------------------------------------------
// Mutation inputs
// ---------------------------------------------------------------------------

/** Statuses in which a contract's content may still be changed. */
const EDITABLE_STATUSES = ['draft', 'generation_completed'];

/**
 * Whether a contract can still be edited.
 *
 * Two conditions, and both matter: a signed PDF exists (signatures are baked
 * in, so the source can never change again), or the status has moved past the
 * pre-send stages. Sending for signature therefore locks a document
 * permanently, since every post-send status falls outside the allowlist.
 */
export const isContractEditable = (
  contract: Pick<OrgContractDocumentItem, 'Status' | 'SigDocumentUrl'> | null | undefined
): boolean => {
  if (!contract) return false;
  if (contract.SigDocumentUrl) return false;
  return !contract.Status || EDITABLE_STATUSES.includes(contract.Status);
};

export interface OrgContractDocumentInsertInput {
  OrganizationId: string;
  SigOrgId?: string; // SELISE Signature (ARC) OrganizationId; set for logged-in users
  GuestSessionId?: string | null;
  OrganizationIds?: string[];
  Name: string;
  TemplateId: string;
  TemplateSlug: string;
  TemplateTitle: string;
  Status: string;
  IsArchived?: boolean;
  FormData?: string;
  EnrichmentContext?: string;
  DynamicFields?: string;
  CurrentStep?: number;
  ProgressPercent?: number;
  Document?: string;
  Signatories?: string;
  Tags?: string[];
  HasSentForApproval?: boolean;
  IsApproved?: boolean;
  Reviewers?: string[];
}

export interface OrgContractDocumentUpdateInput {
  Name?: string;
  TemplateTitle?: string;
  Status?: string;
  IsArchived?: boolean;
  FormData?: string;
  EnrichmentContext?: string;
  DynamicFields?: string;
  CurrentStep?: number;
  ProgressPercent?: number;
  Document?: string;
  Signatories?: string;
  CompletedAt?: string;
  IsDeleted?: boolean;
  GuestSessionId?: string | null;
  SigDocumentId?: string;
  SigDocumentUrl?: string;
  HasSentForApproval?: boolean;
  IsApproved?: boolean;
  Reviewers?: string[];
}

// ---------------------------------------------------------------------------
// Query response
// ---------------------------------------------------------------------------

export interface GetOrgContractDocumentsResponse {
  getOrgContractDocuments: {
    items: OrgContractDocumentItem[];
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    pageSize: number;
    pageNo: number;
    totalPages: number;
  };
}

// ---------------------------------------------------------------------------
// Mutation responses
// ---------------------------------------------------------------------------

export type MutateOrgContractDocumentResponse = MutationResult;

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export type PaginationParams = import('@/graphql/types').ListQueryVariables<Record<string, unknown>>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function parseSignatories(raw: string | null | undefined): ContractSignatory[] {
  if (!raw) return [];
  try {
    return JSON.parse(raw) as ContractSignatory[];
  } catch {
    return [];
  }
}

export function serializeSignatories(entries: ContractSignatory[]): string {
  return JSON.stringify(entries);
}

export function mapSignatoryEntries(entries: SignatoryEntry[]): ContractSignatory[] {
  const now = new Date().toISOString();
  return entries.map((e) => ({
    id: e.id,
    partyType: e.partyType,
    name: e.name,
    email: e.email,
    title: e.title,
    phone: e.phone,
    company: e.company,
    address: e.address,
    status: 'pending',
    updatedTime: now,
  }));
}
